import { Link, matchPath } from "react-router-dom";
import {
  HomeOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { MenuProps } from "antd";
import PeopleIcon from "@mui/icons-material/People";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import ScheduleIcon from "@mui/icons-material/Schedule";
import DashboardIcon from "@mui/icons-material/Dashboard";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";

export type RoleMenuItem = NonNullable<MenuProps["items"]>[number] & {
  roles?: string[];
  children?: RoleMenuItem[];
};

// ✅ Define menu items (unfiltered)
export const menuItems: RoleMenuItem[] = [
  {
    key: "1",
    icon: <HomeOutlined />,
    label: <Link to="/home">Home</Link>,
    roles: ["USER", "ADMIN"],
  },
  {
    key: "2",
    icon: <AdminPanelSettingsIcon />,
    label: "Management",
    roles: ["ADMIN"],
    children: [
      {
        key: "2-1",
        icon: <PeopleIcon />,
        label: <Link to="/management/users">Users</Link>,
        roles: ["ADMIN"],
      },
      {
        key: "2-2",
        icon: <NotificationsNoneIcon />,
        label: <Link to="/management/notifications">Notifications</Link>,
        roles: ["ADMIN"],
      },
      {
        key: "2-3",
        icon: <HistoryOutlined />,
        label: <Link to="/management/changelogs">Changelogs</Link>,
        roles: ["ADMIN"],
      },
    ],
  },
  {
    key: "3",
    icon: <DashboardIcon />,
    label: <Link to="/dashboard">Dashboard</Link>,
    roles: ["USER", "ADMIN"],
  },
  {
    key: "4",
    icon: <LocalHospitalIcon />,
    label: <Link to="/patients">Patients</Link>,
    roles: ["USER", "ADMIN"],
  },
  {
    key: "5",
    icon: <ScheduleIcon />,
    label: <Link to="/appointments">Appointments</Link>,
    roles: ["USER", "ADMIN"],
  },
  {
    key: "6",
    icon: <FormatListBulletedOutlinedIcon />,
    label: <Link to="/changelog">What's New</Link>,
    roles: ["USER", "ADMIN"],
  },
];

// ✅ Utility: filter items by role
export const filterByRoles = (
  items: RoleMenuItem[],
  userRoles: string[]
): RoleMenuItem[] =>
  items
    .map((item) => {
      if (item.children) {
        const filteredChildren = filterByRoles(item.children, userRoles);
        if (filteredChildren.length > 0) {
          return { ...item, children: filteredChildren };
        }
        return null;
      }
      if (!item.roles || item.roles.some((r) => userRoles.includes(r))) {
        return item;
      }
      return null;
    })
    .filter((i): i is RoleMenuItem => i !== null);

// ✅ Utility: map route → menu key
export const pathToKey: Record<string, string> = {
  "/": "1",
  "/home": "1",
  "/management/users": "2-1",
  "/management/notifications": "2-2",
  "/management/changelogs": "2-3",
  "/dashboard": "3",
  "/patients": "4",
  "/examination/:patientId": "4",
  "/appointments": "5",
  "/changelog": "6",
};

// ✅ Utility: find selected key by current path
export const getSelectedKey = (pathname: string): string => {
  for (const [path, key] of Object.entries(pathToKey)) {
    if (matchPath({ path, end: true }, pathname)) return key;
  }
  return "1";
};
